import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useDistanceUnit } from "../contexts/DistanceUnitContext";
import { API_BASE_URL } from "../config";
import ActivityTable from "./ActivityTable";
import { parseActivityDate } from "../utils/dateUtils";

const TotalRunDays = () => {
  const [streakData, setStreakData] = useState(null);
  const [activities, setActivities] = useState([]);
  const [allStreaks, setAllStreaks] = useState([]);
  const [minStreakDays, setMinStreakDays] = useState(7);
  const [loading, setLoading] = useState(true);
  const { unit } = useDistanceUnit();

  const calculateAllStreaks = (activities) => {
    if (!activities || activities.length === 0) return [];

    // Group activities by date and calculate daily distances
    const dailyRuns = {};
    activities.forEach(activity => {
      const date = parseActivityDate(activity);
      if (!date) return;
      const dateKey = date.toISOString().split('T')[0];
      const distance = activity.distance || 0;
      const miles = distance / 1609.34;

      if (!dailyRuns[dateKey]) {
        dailyRuns[dateKey] = 0;
      }
      dailyRuns[dateKey] += miles;
    });

    // Filter days with at least 1 mile and sort
    const streakDays = Object.keys(dailyRuns)
      .filter(day => dailyRuns[day] >= 1.0)
      .sort();

    if (streakDays.length === 0) return [];

    // Calculate all streaks
    const streaks = [];
    let currentStreak = {
      startDate: streakDays[0],
      endDate: streakDays[0],
      days: 1,
      distance: dailyRuns[streakDays[0]]
    };

    for (let i = 1; i < streakDays.length; i++) {
      const prevDate = new Date(streakDays[i - 1]);
      const currDate = new Date(streakDays[i]);
      const daysDiff = (currDate - prevDate) / (1000 * 60 * 60 * 24);

      if (daysDiff === 1) {
        // Continue streak
        currentStreak.endDate = streakDays[i];
        currentStreak.days++;
        currentStreak.distance += dailyRuns[streakDays[i]];
      } else {
        // End current streak and start new one
        streaks.push({ ...currentStreak });
        currentStreak = {
          startDate: streakDays[i],
          endDate: streakDays[i],
          days: 1,
          distance: dailyRuns[streakDays[i]]
        };
      }
    }
    // Add the last streak
    streaks.push(currentStreak);

    // Sort by days (longest first)
    return streaks.sort((a, b) => b.days - a.days);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const user = JSON.parse(localStorage.getItem("user") || "{}");

        // Fetch streak data
        const streakResponse = await axios.get(`${API_BASE_URL}/runstreak`, {
          params: { email: user.email },
          withCredentials: true
        });
        setStreakData(streakResponse.data);

        // Fetch all activities
        const activitiesResponse = await axios.get(`${API_BASE_URL}/activities`, {
          params: { email: user.email },
          withCredentials: true
        });

        const fetchedActivities = activitiesResponse.data.activities;
        setActivities(fetchedActivities);

        // Calculate all streaks
        const streaks = calculateAllStreaks(fetchedActivities);
        setAllStreaks(streaks);

        setLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return <div className="container"><div className="card"><p>Loading...</p></div></div>;
  }

  if (!streakData || streakData.total_run_days === 0) {
    return (
      <div className="container">
        <div className="card">
          <h1>📅 Total Run Days</h1>
          <p>You haven't logged any runs yet. Start tracking your activities!</p>
          <Link to="/dashboard">← Back to Dashboard</Link>
        </div>
      </div>
    );
  }

  const formatDate = (dateStr) => {
    if (!dateStr) return "N/A";
    return new Date(dateStr).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
  };

  const convertMilesToUnit = (miles) => {
    if (!miles) return 0;
    if (unit === 'km') {
      return (miles * 1.60934).toFixed(2);
    }
    return miles;
  };

  return (
    <div className="container">
      <div className="card">
        <Link to="/dashboard" style={{ textDecoration: 'none', color: '#14b8a6', marginBottom: '20px', display: 'inline-block' }}>
          ← Back to Dashboard
        </Link>
        <h1>📅 Total Run Days</h1>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', margin: '20px 0' }}>
          <div style={{ textAlign: 'center', padding: '20px', background: '#faf5ff', borderRadius: '12px' }}>
            <h3 style={{ color: '#8b5cf6', fontSize: '48px', margin: '0' }}>{streakData.total_run_days}</h3>
            <p style={{ color: '#6b7280', margin: '8px 0' }}>Total Days</p>
          </div>
          <div style={{ textAlign: 'center', padding: '20px', background: '#faf5ff', borderRadius: '12px' }}>
            <h3 style={{ color: '#8b5cf6', fontSize: '48px', margin: '0' }}>{convertMilesToUnit(streakData.total_distance_miles)}</h3>
            <p style={{ color: '#6b7280', margin: '8px 0' }}>{unit === 'km' ? 'Total Kilometers' : 'Total Miles'}</p>
          </div>
          <div style={{ textAlign: 'center', padding: '20px', background: '#faf5ff', borderRadius: '12px' }}>
            <h3 style={{ color: '#8b5cf6', fontSize: '48px', margin: '0' }}>{activities.length}</h3>
            <p style={{ color: '#6b7280', margin: '8px 0' }}>Total Activities</p>
          </div>
        </div>

        <div style={{ margin: '20px 0', padding: '20px', background: '#f9fafb', borderRadius: '12px' }}>
          <h3>Running Summary</h3>
          <p>🏃 Last run: {formatDate(streakData.last_run_date)}</p>
          <p style={{ color: '#8b5cf6', fontWeight: '600', marginTop: '12px' }}>
            📊 You've run at least 1 mile on {streakData.total_run_days} different days!
          </p>
        </div>
      </div>

      <div className="card">
        <h2>All Streaks ({allStreaks.filter(s => s.days >= minStreakDays).length})</h2>
        <div style={{ marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px' }}>
          <label htmlFor="minDays" style={{ fontWeight: '500' }}>Minimum days:</label>
          <select
            id="minDays"
            value={minStreakDays}
            onChange={(e) => setMinStreakDays(Number(e.target.value))}
            style={{
              padding: '8px 12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              background: 'white',
              cursor: 'pointer'
            }}
          >
            <option value="1">1+ days</option>
            <option value="3">3+ days</option>
            <option value="5">5+ days</option>
            <option value="7">7+ days</option>
            <option value="14">14+ days</option>
            <option value="30">30+ days</option>
            <option value="60">60+ days</option>
            <option value="90">90+ days</option>
          </select>
          <span style={{ color: '#6b7280', fontSize: '14px' }}>
            Showing streaks of {minStreakDays}+ consecutive days
          </span>
        </div>

        {allStreaks.filter(s => s.days >= minStreakDays).length === 0 ? (
          <p style={{ color: '#6b7280', textAlign: 'center', padding: '40px' }}>
            No streaks found with {minStreakDays}+ days. Try lowering the filter.
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>#</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Days</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>Start Date</th>
                  <th style={{ padding: '12px', textAlign: 'left', fontWeight: '600' }}>End Date</th>
                  <th style={{ padding: '12px', textAlign: 'right', fontWeight: '600' }}>Distance</th>
                </tr>
              </thead>
              <tbody>
                {allStreaks
                  .filter(s => s.days >= minStreakDays)
                  .map((streak, index) => (
                    <tr
                      key={index}
                      style={{
                        borderBottom: '1px solid #e5e7eb',
                        background: index === 0 ? '#fffbeb' : 'white'
                      }}
                    >
                      <td style={{ padding: '12px', color: '#6b7280' }}>
                        {index === 0 ? '🏆' : index + 1}
                      </td>
                      <td style={{
                        padding: '12px',
                        fontWeight: index === 0 ? '600' : '400',
                        color: index === 0 ? '#f59e0b' : '#111827'
                      }}>
                        {streak.days} {streak.days === 1 ? 'day' : 'days'}
                      </td>
                      <td style={{ padding: '12px', color: '#6b7280' }}>
                        {formatDate(streak.startDate)}
                      </td>
                      <td style={{ padding: '12px', color: '#6b7280' }}>
                        {formatDate(streak.endDate)}
                      </td>
                      <td style={{
                        padding: '12px',
                        textAlign: 'right',
                        fontWeight: '500',
                        color: '#111827'
                      }}>
                        {convertMilesToUnit(streak.distance)} {unit === 'km' ? 'km' : 'mi'}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="card">
        <h2>All Activities ({activities.length})</h2>
        <ActivityTable activities={activities} showDateFilter={true} />
      </div>
    </div>
  );
};

export default TotalRunDays;
